import type { FC } from "react";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { getWorkerIdForUser } from "../../services/workers";
import { getWorkerTeamContext } from "../../services/teams";
import DateRangePicker from "../DateRangePicker";

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface DashboardHeaderProps {
  viewMode: "all" | "individual";
  setViewMode: (mode: "all" | "individual") => void;
  selectedOption: "today" | "yesterday" | "custom";
  setSelectedOption: (option: "today" | "yesterday" | "custom") => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  isAdminRole: boolean;
}

const DashboardHeader: FC<DashboardHeaderProps> = ({
  viewMode,
  setViewMode,
  selectedOption,
  setSelectedOption,
  // @ts-expect-error - Suppressing TS error for unused parameter
  dateRange,
  setDateRange,
  isAdminRole,
}) => {
  const [workerName, setWorkerName] = useState<string | null>(null);
  const [teamContext, setTeamContext] = useState<{
    teamCount: number;
    hasMultipleTeams: boolean;
    hasNoTeams: boolean;
    teams: Array<{ id: number; name: string; }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchWorkerData = useCallback(async () => {
    try {
      setError(null);
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: workerData, error: workerError } = await supabase
        .from("workers")
        .select("id, full_name")
        .eq("user_id", user.id)
        .single();

      if (workerError) throw workerError;
      if (!workerData) {
        setError("No worker found for user");
        return;
      }

      setWorkerName(workerData.full_name);

      // Fetch team context for the worker
      const workerId = await getWorkerIdForUser(user.id);
      console.log('DashboardHeader: Worker ID for user:', workerId);
      const context = await getWorkerTeamContext(workerId);
      console.log('DashboardHeader: Team context received:', context);
      setTeamContext(context);
    } catch (err) {
      console.error("Error fetching worker data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch worker data"
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchWorkerData();
  }, [fetchWorkerData]);

  const formatDate = () => {
    const now = new Date();
    const days = [
      "Sonntag",
      "Montag",
      "Dienstag",
      "Mittwoch",
      "Donnerstag",
      "Freitag",
      "Samstag",
    ];
    const months = [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember",
    ];

    const day = days[now.getDay()];
    const month = months[now.getMonth()];
    const date = now.getDate();
    const year = now.getFullYear();

    return `${day}, ${month} ${date}, ${year}`;
  };

  const formatTeamInfo = () => {
    if (!teamContext) return null;

    if (teamContext.hasNoTeams) {
      return "Kein Team zugewiesen";
    } else if (teamContext.hasMultipleTeams) {
      return `${teamContext.teamCount} Teams: ${teamContext.teams.map(t => t.name).join(", ")}`;
    } else if (teamContext.teamCount === 1) {
      return `Team: ${teamContext.teams[0]?.name || "Unbekannt"}`;
    }

    return null;
  };

  if (loading) {
    return (
      <div className="mb-4 animate-pulse">
        <div className="h-8 w-64 rounded bg-gray-200 dark:bg-gray-700"></div>
        <div className="mt-2 h-6 w-48 rounded bg-gray-200 dark:bg-gray-700"></div>
        <div className="mt-1 h-4 w-56 rounded bg-gray-200 dark:bg-gray-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 text-red-600 dark:text-red-500">Error: {error}</div>
    );
  }

  return (
    <div className="mb-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-3">
        {/* Worker Greeting */}
        <div className="order-1 flex flex-col justify-center lg:col-span-2 lg:mb-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Hallo {workerName}!
          </h1>
          <p className="mt-1 text-base text-gray-500 dark:text-gray-400">
            {formatDate()}
          </p>
          {teamContext && formatTeamInfo() && (
            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
              {formatTeamInfo()}
            </p>
          )}
        </div>

        {isAdminRole ? (
          <>
            {/* Revenue selector - completely redesigned for better reliability */}
            <div className="order-3 mt-4 flex items-center justify-start lg:order-2 lg:col-span-2 lg:mt-0 lg:justify-end">
              <div className="inline-flex rounded-full bg-gray-200 p-0.5 dark:bg-gray-700">
                <button
                  type="button"
                  onClick={() => setViewMode("all")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                    viewMode === "all"
                      ? "bg-white shadow dark:bg-gray-600 dark:text-white"
                      : "text-gray-600 hover:bg-gray-300 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  Alle Mitarbeiter
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("individual")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                    viewMode === "individual"
                      ? "bg-white shadow dark:bg-gray-600 dark:text-white"
                      : "text-gray-600 hover:bg-gray-300 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  Mein Umsatz
                </button>
              </div>
            </div>

            {/* Leaderboard selector - aligned to the left */}
            <div className="order-2 mt-4 hidden items-center justify-start lg:order-3 lg:mt-0 lg:flex">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedOption("today");
                    setDateRange({
                      startDate: new Date(),
                      endDate: new Date(),
                    });
                  }}
                  className={`h-[38px] w-full rounded-lg px-4 py-2 text-sm font-medium ${
                    selectedOption === "today"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-900 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  }`}
                >
                  Heute
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedOption("yesterday");
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    setDateRange({ startDate: yesterday, endDate: yesterday });
                  }}
                  className={`h-[38px] w-full rounded-lg px-4 py-2 text-sm font-medium ${
                    selectedOption === "yesterday"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-900 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  }`}
                >
                  Gestern
                </button>
                <div className="w-full">
                  <DateRangePicker
                    onChange={(range) => {
                      setDateRange(range);
                      setSelectedOption("custom");
                    }}
                    label=""
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="order-2 hidden lg:block"></div>
        )}
      </div>
    </div>
  );
};

export default DashboardHeader;
